import { useEffect, useState, useRef } from 'react';
import api from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { MapPin, User, Clock, Eye } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_CONFIG = {
  active: { label: 'Active', color: '#2ecc71', dot: 'bg-green-500' },
  weak: { label: 'Weak Signal', color: '#f5a623', dot: 'bg-amber-500' },
  lost: { label: 'Signal Lost', color: '#e74c3c', dot: 'bg-red-500' },
  no_data: { label: 'No Data', color: '#7a8fa8', dot: 'bg-gray-400' },
  unknown: { label: 'Unknown', color: '#7a8fa8', dot: 'bg-gray-400' },
};

export default function DriverTracking() {
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  const fetchDrivers = async () => {
    try {
      const res = await api.get('/routeops/drivers/live-status');
      setDrivers(res.data || []);
    } catch {
      toast.error('Failed to load driver status');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDrivers();
    const iv = setInterval(fetchDrivers, 30000); // refresh every 30s
    return () => clearInterval(iv);
  }, []);

  const viewHistory = async (driver) => {
    setSelectedDriver(driver);
    setHistoryLoading(true);
    try {
      const res = await api.get(`/routeops/drivers/${driver.id}/location-history`);
      setHistory(res.data.history || []);
    } catch {
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  // Render map when history loads
  useEffect(() => {
    if (!selectedDriver || !history.length || !mapRef.current) return;

    const timer = setTimeout(() => {
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }

      const L = window.L;
      if (!L || !mapRef.current) return;

      mapRef.current.innerHTML = '';
      const container = document.createElement('div');
      container.style.cssText = 'width:100%;height:100%;';
      mapRef.current.appendChild(container);

      const map = L.map(container).setView([52.48, -1.90], 10);
      mapInstanceRef.current = map;

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OSM', maxZoom: 18,
      }).addTo(map);

      const points = history.map(h => [h.lat, h.lng]);
      if (points.length > 1) {
        L.polyline(points, { color: '#3498db', weight: 3, opacity: 0.7 }).addTo(map);
      }

      // Start marker
      if (points.length > 0) {
        L.circleMarker(points[0], { radius: 6, color: '#2ecc71', fillOpacity: 1 }).addTo(map).bindPopup('Start');
        L.circleMarker(points[points.length - 1], { radius: 8, color: '#e74c3c', fillOpacity: 1 }).addTo(map).bindPopup('Latest');
        map.fitBounds(points, { padding: [30, 30] });
      }
    }, 200);

    return () => {
      clearTimeout(timer);
      if (mapInstanceRef.current) { mapInstanceRef.current.remove(); mapInstanceRef.current = null; }
    };
  }, [history, selectedDriver]);

  const fmtTime = (ts) => {
    if (!ts) return '—';
    return new Date(ts).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  };

  const fmtAge = (secs) => {
    if (!secs) return '—';
    if (secs < 60) return `${secs}s ago`;
    if (secs < 3600) return `${Math.floor(secs / 60)}m ago`;
    return `${Math.floor(secs / 3600)}h ago`;
  };

  return (
    <div data-testid="driver-tracking-page">
      <div className="flex items-center gap-3 pb-3 mb-4 border-b" style={{ borderColor: 'var(--border-color)' }}>
        <MapPin className="h-5 w-5" style={{ color: 'var(--amber)' }} />
        <h1 className="text-2xl font-bold" style={{ color: 'var(--text-primary)', fontFamily: 'var(--fd)' }}>Driver Tracking</h1>
        <span className="text-xs" style={{ color: 'var(--text-muted)' }}>Live — refreshes every 30s</span>
      </div>

      {/* Driver status grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 mb-6">
        {loading && <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading...</p>}
        {drivers.map(d => {
          const cfg = STATUS_CONFIG[d.location_status] || STATUS_CONFIG.unknown;
          return (
            <Card key={d.id} className="hover:shadow-md transition-shadow cursor-pointer" onClick={() => viewHistory(d)}
              data-testid={`driver-card-${d.id}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <User className="h-4 w-4" style={{ color: 'var(--text-muted)' }} />
                    <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{d.full_name}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className={`w-2.5 h-2.5 rounded-full ${cfg.dot} ${d.location_status === 'active' ? 'animate-pulse' : ''}`} />
                    <span className="text-[10px] font-bold uppercase" style={{ color: cfg.color, fontFamily: 'var(--fm)' }}>{cfg.label}</span>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-[10px]" style={{ color: 'var(--text-muted)', fontFamily: 'var(--fm)' }}>
                  {d.last_lat ? (
                    <>
                      <span>{d.last_lat.toFixed(4)}, {d.last_lng.toFixed(4)}</span>
                      <span>{fmtAge(d.age_seconds)}</span>
                    </>
                  ) : (
                    <span>No location data</span>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
        {!loading && drivers.length === 0 && (
          <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No drivers found in the system.</p>
        )}
      </div>

      {/* Selected driver history */}
      {selectedDriver && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Map */}
          <Card>
            <CardContent className="p-0">
              <div className="px-4 py-2 flex items-center justify-between" style={{ borderBottom: '1px solid var(--border-color)' }}>
                <div>
                  <span className="text-sm font-bold" style={{ color: 'var(--text-primary)' }}>{selectedDriver.full_name}</span>
                  <span className="text-xs ml-2" style={{ color: 'var(--text-muted)' }}>Today's movement</span>
                </div>
                <button onClick={() => { setSelectedDriver(null); setHistory([]); }} style={{ color: 'var(--text-muted)' }}>
                  &times;
                </button>
              </div>
              <div ref={mapRef} style={{ height: '300px' }} />
            </CardContent>
          </Card>

          {/* History log */}
          <Card>
            <CardContent className="p-4">
              <h3 className="text-xs font-bold uppercase tracking-wider mb-3" style={{ color: 'var(--text-secondary)', fontFamily: 'var(--fd)' }}>
                Location Log — {history.length} updates today
              </h3>
              <div className="max-h-[280px] overflow-y-auto space-y-1">
                {historyLoading && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>Loading...</p>}
                {!historyLoading && history.length === 0 && <p className="text-xs" style={{ color: 'var(--text-muted)' }}>No location updates today.</p>}
                {history.map((h, i) => (
                  <div key={i} className="flex items-center gap-3 px-2 py-1 rounded text-[10px]" style={{ background: i % 2 === 0 ? 'var(--bg-raised)' : 'transparent', fontFamily: 'var(--fm)' }}>
                    <Clock className="h-3 w-3 shrink-0" style={{ color: 'var(--text-muted)' }} />
                    <span style={{ color: 'var(--amber)' }}>{fmtTime(h.timestamp)}</span>
                    <span style={{ color: 'var(--text-secondary)' }}>{h.lat.toFixed(5)}, {h.lng.toFixed(5)}</span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
